import React, { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import BottomDrawer from "common/Drawers/BottomDrawer";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import TradeBarDetails from "common/NewLook/TradeBarDetails.tsx";
import { LadderKind, LevelsLadderModel } from "common/NewLook/priceLadder.ts";
import {
  getTradeBarOrders,
  TradeBarPoints,
} from "common/NewLook/tradeBarLayout.ts";
import {
  getDefaultTradeBarTarget,
  TradeBarTarget,
  TradeBarTargetKind,
} from "common/NewLook/tradeBarHover.ts";

interface TradeBarSheetProps {
  open: boolean;
  onClose: () => void;
  model: LevelsLadderModel;
  points: TradeBarPoints;
  target: TradeBarTarget;
  onTargetChange: (target: TradeBarTarget) => void;
  formatPrice: (price: number) => string;
  toneColor: (tone: LadderKind) => string;
  bar: ReactNode;
}

const TradeBarSheet = ({
  open,
  onClose,
  model,
  points,
  target,
  onTargetChange,
  formatPrice,
  toneColor,
  bar,
}: TradeBarSheetProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => setShowAll(false), [target.kind]);
  const tabs = TABS.filter((kind) => kind !== "price" || !!points.price);
  return (
    <BottomDrawer
      title={t("trade_bar.sheet_title")}
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      onClose={onClose}
      contentStyle={CONTENT_STYLE}
      content={
        <Content>
          <BarSlot>{bar}</BarSlot>
          <TabsSlot>
            <SegmentTabs
              options={tabs}
              value={target.kind}
              onChange={(kind) =>
                onTargetChange(
                  getDefaultTradeBarTarget(getTradeBarOrders(model), kind)
                )
              }
              ariaLabel={t("trade_bar.tabs_label")}
              getLabel={(kind) => t(TAB_LABELS[kind])}
            />
          </TabsSlot>
          <DetailsScroll>
            <TradeBarDetails
              model={model}
              points={points}
              target={target}
              formatPrice={formatPrice}
              toneColor={toneColor}
              size="sheet"
              showAll={showAll}
              onShowAllChange={setShowAll}
            />
          </DetailsScroll>
        </Content>
      }
    />
  );
};

export default TradeBarSheet;

const SHEET_GUTTER = 20;
// BottomDrawer caps the sheet; only DetailsScroll scrolls inside it.
const CONTENT_STYLE = {
  overflow: "hidden",
  paddingBottom: 0,
};
const TABS: TradeBarTargetKind[] = ["entry", "price", "take_profit", "stop"];
const TAB_LABELS = {
  entry: "trade_bar.tab_entries",
  price: "trade_bar.tab_price",
  take_profit: "trade_bar.tab_take_profit",
  stop: "trade_bar.tab_stop",
} as const;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;
const DetailsScroll = styled.div`
  flex: 1;
  min-height: 0;
  margin: 0 -${SHEET_GUTTER}px;
  padding: 0 ${SHEET_GUTTER}px ${SHEET_GUTTER}px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;
const BarSlot = styled.div`
  flex: none;
  padding: 4px 0 8px;
`;
const TabsSlot = styled.div`
  flex: none;
  display: flex;
  margin: 8px 0 18px;
  & > [role="tablist"] {
    flex: 1;
  }
  & [role="tab"] {
    flex: 1;
  }
`;
